import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Spinner, Alert, Badge, Form } from 'react-bootstrap';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { hiringDirectionsApiService } from '../../services/apiServices';
import { RecruitingDirectionDto } from '../../api-client';

const HiringDirectionsDetailPage: React.FC = () => {
  const { directionId } = useParams<{ directionId: string }>();
  const navigate = useNavigate();

  const [direction, setDirection] = useState<RecruitingDirectionDto | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [updating, setUpdating] = useState<boolean>(false);
  const [updateError, setUpdateError] = useState<string | null>(null);
  const [editMode, setEditMode] = useState<boolean>(false);
  const [editName, setEditName] = useState<string>('');
  const [editDescription, setEditDescription] = useState<string>('');

  useEffect(() => {
    if (!directionId) {
      navigate('/hiring/directions');
      return;
    }

    const loadDirection = async () => {
      try {
        setLoading(true);
        const response = await hiringDirectionsApiService.getDirection(directionId);
        const directionData = response.data || null;
        setDirection(directionData);
        if (directionData) {
          setEditName(directionData.name);
          setEditDescription(directionData.description || '');
        }
      } catch (err: any) {
        console.error('Ошибка при загрузке направления:', err);
        setError(err.message || 'Ошибка при загрузке направления');
      } finally {
        setLoading(false);
      }
    };

    loadDirection();
  }, [directionId, navigate]);

  const handleUpdateDirection = async () => {
    if (!directionId || !editName.trim()) return;

    try {
      setUpdating(true);
      setUpdateError(null);
      await hiringDirectionsApiService.updateDirection(
        directionId,
        editName.trim(),
        editDescription.trim() || null
      );

      // Обновляем локальное состояние
      setDirection((prev) =>
        prev
          ? {
              ...prev,
              name: editName.trim(),
              description: editDescription.trim() || null,
            }
          : null
      );
      setEditMode(false);
    } catch (err: any) {
      console.error('Ошибка при обновлении направления:', err);
      setUpdateError(err.message || 'Ошибка при обновлении направления');
    } finally {
      setUpdating(false);
    }
  };

  const handleActivateDirection = async () => {
    if (!directionId) return;

    try {
      setUpdating(true);
      setUpdateError(null);
      await hiringDirectionsApiService.activateDirection(directionId);

      setDirection((prev) => (prev ? { ...prev, is_active: true } : null));
    } catch (err: any) {
      console.error('Ошибка при активации направления:', err);
      setUpdateError(err.message || 'Ошибка при активации направления');
    } finally {
      setUpdating(false);
    }
  };

  const handleDeactivateDirection = async () => {
    if (!directionId) return;

    try {
      setUpdating(true);
      setUpdateError(null);
      await hiringDirectionsApiService.deactivateDirection(directionId);

      setDirection((prev) => (prev ? { ...prev, is_active: false } : null));
    } catch (err: any) {
      console.error('Ошибка при деактивации направления:', err);
      setUpdateError(err.message || 'Ошибка при деактивации направления');
    } finally {
      setUpdating(false);
    }
  };

  const handleCancelEdit = () => {
    if (direction) {
      setEditName(direction.name);
      setEditDescription(direction.description || '');
    }
    setEditMode(false);
    setUpdateError(null);
  };

  if (loading) {
    return (
      <Container
        fluid
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: '50vh' }}
      >
        <div className="text-center">
          <Spinner animation="border" />
          <p className="mt-2">Загрузка направления...</p>
        </div>
      </Container>
    );
  }

  if (error || !direction) {
    return (
      <Container fluid>
        <Row className="mt-4">
          <Col>
            <Alert variant="danger">{error || 'Направление не найдено'}</Alert>
            <Button variant="primary" onClick={() => navigate('/hiring/directions')}>
              Вернуться к списку
            </Button>
          </Col>
        </Row>
      </Container>
    );
  }

  return (
    <Container fluid>
      <Row className="mt-4">
        <Col>
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h2>Направление: {direction.name}</h2>
            <div>
              <Link to="/hiring/directions">
                <Button variant="outline-secondary" className="me-2">
                  Вернуться к списку
                </Button>
              </Link>
              {!editMode && (
                <Button variant="primary" onClick={() => setEditMode(true)}>
                  Редактировать
                </Button>
              )}
            </div>
          </div>

          {updateError && <Alert variant="danger">{updateError}</Alert>}

          <Card>
            <Card.Header>
              <Badge bg={direction.is_active ? 'success' : 'secondary'}>
                {direction.is_active ? 'Активно' : 'Неактивно'}
              </Badge>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col md={6}>
                  <h5>Основная информация</h5>
                  <table className="table table-bordered">
                    <tbody>
                      <tr>
                        <th style={{ width: '40%' }}>ID направления</th>
                        <td>{direction.id}</td>
                      </tr>
                      <tr>
                        <th>Название</th>
                        <td>
                          {editMode ? (
                            <Form.Control
                              type="text"
                              value={editName}
                              onChange={(e) => setEditName(e.target.value)}
                            />
                          ) : (
                            <strong>{direction.name}</strong>
                          )}
                        </td>
                      </tr>
                      <tr>
                        <th>Статус</th>
                        <td>
                          <Badge bg={direction.is_active ? 'success' : 'secondary'}>
                            {direction.is_active ? 'Активно' : 'Неактивно'}
                          </Badge>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </Col>

                <Col md={6}>
                  <h5>Описание</h5>
                  {editMode ? (
                    <Form.Group className="mb-3">
                      <Form.Control
                        as="textarea"
                        rows={5}
                        value={editDescription}
                        onChange={(e) => setEditDescription(e.target.value)}
                        placeholder="Введите описание направления"
                      />
                    </Form.Group>
                  ) : (
                    <p className="text-muted">{direction.description || 'Описание отсутствует'}</p>
                  )}

                  {editMode ? (
                    <div className="d-grid gap-2">
                      <Button
                        variant="success"
                        onClick={handleUpdateDirection}
                        disabled={updating || !editName.trim()}
                      >
                        {updating ? (
                          <>
                            <Spinner animation="border" size="sm" className="me-2" />
                            Сохранение...
                          </>
                        ) : (
                          'Сохранить изменения'
                        )}
                      </Button>
                      <Button
                        variant="outline-secondary"
                        onClick={handleCancelEdit}
                        disabled={updating}
                      >
                        Отмена
                      </Button>
                    </div>
                  ) : (
                    <div className="d-grid gap-2">
                      {direction.is_active ? (
                        <Button
                          variant="outline-warning"
                          onClick={handleDeactivateDirection}
                          disabled={updating}
                        >
                          Деактивировать направление
                        </Button>
                      ) : (
                        <Button
                          variant="outline-success"
                          onClick={handleActivateDirection}
                          disabled={updating}
                        >
                          Активировать направление
                        </Button>
                      )}
                    </div>
                  )}
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default HiringDirectionsDetailPage;
